import React from "react";
import { Bar, CartesianGrid, Cell, ComposedChart, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { measureValue, type ChatSurveyTrends, type TrendGroup, type TrendMeasure, type TrendStats } from "~shared/chatSurveyTrends";

export const LIKE_COLOR = "#2a78d6";
export const FINE_COLOR = "#a3a7b0";
export const DISLIKE_COLOR = "#e34948";
const MARKER_COLOR = "#8a4fbf";
export const LOW_SAMPLE = 20;

export type ChartLine = { group: TrendGroup; color: string };
export type ChartPick = { index: number; group: TrendGroup | null };

export function formatMeasure(value: number | null, measure: TrendMeasure): string {
  if (value == null) return "—";
  if (measure === "responseMs") return formatSeconds(value);
  if (measure === "net") return `${value > 0.5 ? "+" : value < -0.5 ? "−" : ""}${Math.abs(value).toFixed(0)}`;
  return `${value.toFixed(0)}%`;
}

export function formatSeconds(ms: number | null): string {
  if (ms == null) return "—";
  const seconds = ms / 1000;
  return seconds >= 90 ? `${(seconds / 60).toFixed(1)} min` : `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
}


type Props = {
  trends: ChatSurveyTrends;
  labels: string[];
  titles: string[];
  measure: TrendMeasure;
  lines: ChartLine[] | null;
  /** The bucket in which reactions replaced stars. */
  marker: { index: number } | null;
  onPick: (pick: ChartPick) => void;
};

function TooltipBox({ title, rows, notes }: { title: string; rows: Array<{ color: string; value: string; label: string }>; notes: Array<string | null> }) {
  return (
    <div className="rounded-lg border border-solid border-app-menu-color-hover bg-app-menu-color px-3 py-2 text-xs shadow-card max-w-xs">
      <div className="mb-1 font-semibold text-app-grey-1">{title}</div>
      {rows.map((row) => (
        <div key={row.label} className="flex items-center gap-2 tabular-nums">
          <span className="inline-block h-0.5 w-3 rounded" style={{ background: row.color }} />
          <span className="font-semibold text-app-grey-0">{row.value}</span>
          <span className="truncate text-app-grey-2">{row.label}</span>
        </div>
      ))}
      {notes.filter(Boolean).map((note) => <div key={note} className="mt-1 text-app-grey-2">{note}</div>)}
    </div>
  );
}

function Dot({ cx, cy, color, hollow, onClick }: { cx?: number; cy?: number; color: string; hollow: boolean; onClick: () => void }) {
  if (cx == null || cy == null) return <g />;
  return (
    <g onClick={onClick} style={{ cursor: "pointer" }}>
      <circle cx={cx} cy={cy} r={12} fill="transparent" />
      <circle cx={cx} cy={cy} r={4} fill={hollow ? "var(--menu-color)" : color} stroke={hollow ? color : "var(--menu-color)"} strokeWidth={2} />
    </g>
  );
}

const axisTick = { fontSize: 11, fill: "var(--grey-1)" };

/** Evenly spaced ticks on a round step that always include 0 when the range spans it. */
function niceAxis(min: number, max: number, maxTicks = 6): { domain: [number, number]; ticks: number[] } {
  const span = Math.max(max - min, 1);
  const raw = span / maxTicks;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 2.5, 5, 10].find((m) => m * magnitude >= raw) ?? 10) * magnitude;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let value = lo; value <= hi + step / 2; value += step) ticks.push(Math.round(value * 1000) / 1000);
  return { domain: [lo, hi], ticks };
}

export function ChatSurveyTrendsChart({ trends, labels, titles, measure, lines, marker, onPick }: Props) {
  const last = trends.buckets.length - 1;
  // A period cut short by the range (first) or still filling in (last) is drawn dashed or faded.
  const partialFirst = trends.partialFirst && last > 0;
  const partialLast = trends.partialLast && last > 0;
  const isPartial = (index: number) => (partialFirst && index === 0) || (partialLast && index === last);
  const nearPartial = (index: number) => (partialFirst && index <= 1) || (partialLast && index >= last - 1);
  // Bucket position is the axis key, since labels like "13:00" repeat across days.
  const xAxis = <XAxis dataKey="index" type="category" tick={axisTick} minTickGap={24} tickFormatter={(index) => labels[Number(index)] ?? ""} />;
  const markerLine = marker ? (
    <ReferenceLine
      x={marker.index}
      stroke={MARKER_COLOR}
      strokeDasharray="4 3"
      label={{ value: "Stars → reactions", position: "insideTopRight", fill: MARKER_COLOR, fontSize: 10, fontWeight: 600 }}
    />
  ) : null;

  if (!lines) {
    const data = trends.overall.points.map((stats, index) => {
      const share = (key: "like" | "fine" | "dislike") => (stats.answered ? (stats[key] / stats.answered) * 100 : 0);
      return {
        index, label: labels[index],
        like: share("like"), fineUp: share("fine") / 2, fineDown: -share("fine") / 2, dislike: -share("dislike"),
      };
    });
    const up = Math.max(20, ...data.map((d) => d.like + d.fineUp));
    const down = Math.max(20, ...data.map((d) => -d.dislike + d.fineUp));
    const { domain, ticks } = niceAxis(-down, up);
    const fade = (index: number) => (isPartial(index) ? 0.45 : 1);
    return (
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart
          data={data}
          stackOffset="sign"
          margin={{ top: 18, right: 16, left: 0, bottom: 4 }}
          onClick={(state: any) => { if (state?.activeTooltipIndex != null) onPick({ index: state.activeTooltipIndex, group: null }); }}
          style={{ cursor: "pointer" }}
        >
          <CartesianGrid vertical={false} strokeOpacity={0.25} />
          {xAxis}
          <YAxis domain={domain} ticks={ticks} interval={0} tickFormatter={(value) => `${Math.abs(Number(value))}%`} tick={axisTick} width={44} />
          <ReferenceLine y={0} stroke="var(--grey-2)" />
          {markerLine}
          <Tooltip
            cursor={{ fill: "var(--grey-1)", fillOpacity: 0.08 }}
            wrapperStyle={{ outline: "none" }}
            content={({ active, payload }: any) => {
              if (!active || !payload?.length) return null;
              const index = payload[0].payload.index as number;
              const stats = trends.overall.points[index];
              return (
                <TooltipBox
                  title={titles[index]}
                  rows={[
                    { color: LIKE_COLOR, value: formatMeasure(measureValue(stats, "like"), "like"), label: "Like" },
                    { color: FINE_COLOR, value: formatMeasure(measureValue(stats, "fine"), "fine"), label: "It's fine" },
                    { color: DISLIKE_COLOR, value: formatMeasure(measureValue(stats, "dislike"), "dislike"), label: "Dislike" },
                  ]}
                  notes={[`${stats.answered} answers`, stats.answered < LOW_SAMPLE ? "Few answers: treat as a hint" : null]}
                />
              );
            }}
          />
          {([["fineUp", FINE_COLOR], ["like", LIKE_COLOR], ["fineDown", FINE_COLOR], ["dislike", DISLIKE_COLOR]] as const).map(([key, color]) => (
            <Bar key={key} dataKey={key} stackId="mix" fill={color} maxBarSize={24} isAnimationActive={false} radius={key === "like" ? [4, 4, 0, 0] : 0}>
              {data.map((d) => <Cell key={d.index} fillOpacity={fade(d.index)} />)}
            </Bar>
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  const sampleOf = (stats: TrendStats) => (measure === "responseMs" ? stats.total : stats.answered);
  const data = trends.buckets.map((_, index) => {
    const point: Record<string, number | string | null> = { index, label: labels[index] };
    lines.forEach(({ group }, line) => {
      const value = measureValue(group.points[index], measure);
      point[`v${line}`] = isPartial(index) ? null : value;
      point[`p${line}`] = nearPartial(index) ? value : null;
    });
    return point;
  });
  const values = lines.flatMap(({ group }) => group.points.map((stats) => measureValue(stats, measure))).filter((value): value is number => value != null);
  const axis = measure === "net"
    ? niceAxis(Math.max(-100, Math.min(0, ...values)), Math.min(100, Math.max(0, ...values)))
    : niceAxis(0, Math.max(measure === "responseMs" ? 1000 : 10, ...values));
  const dot = (line: number, onlyPartial: boolean) => (props: any) => {
    const { group, color } = lines[line];
    if (onlyPartial && !isPartial(props.index)) return <g key={props.index} />;
    return <Dot key={props.index} cx={props.cx} cy={props.cy} color={color} hollow={sampleOf(group.points[props.index]) < LOW_SAMPLE} onClick={() => onPick({ index: props.index, group })} />;
  };
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 18, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid vertical={false} strokeOpacity={0.25} />
        {xAxis}
        <YAxis
          domain={axis.domain}
          ticks={axis.ticks}
          interval={0}
          tickFormatter={(value) => (measure === "responseMs" ? `${Math.round(Number(value) / 1000)}s` : measure === "net" ? String(value) : `${value}%`)}
          tick={axisTick}
          width={44}
        />
        {measure === "net" && <ReferenceLine y={0} stroke="var(--grey-2)" />}
        {markerLine}
        <Tooltip
          wrapperStyle={{ outline: "none" }}
          content={({ active, payload }: any) => {
            if (!active || !payload?.length) return null;
            const index = payload[0].payload.index as number;
            const rows = lines
              .map(({ group, color }) => ({ group, color, value: measureValue(group.points[index], measure) }))
              .filter((row) => row.value != null)
              .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
              .map(({ group, color, value }) => ({ color, value: formatMeasure(value, measure), label: `${group.label} · n=${sampleOf(group.points[index])}` }));
            return <TooltipBox title={titles[index]} rows={rows} notes={[]} />;
          }}
        />
        {lines.flatMap(({ color }, line) => [
          <Line key={`v${line}`} dataKey={`v${line}`} stroke={color} strokeWidth={2} isAnimationActive={false} connectNulls dot={dot(line, false)} activeDot={false} />,
          <Line key={`p${line}`} dataKey={`p${line}`} stroke={color} strokeWidth={2} strokeDasharray="4 4" isAnimationActive={false} dot={dot(line, true)} activeDot={false} legendType="none" />,
        ])}
      </LineChart>
    </ResponsiveContainer>
  );
}
